import React, { useState, useEffect, useCallback } from "react";
import {
  Form, Input, Button, Col, Row, Select, Upload,
  message, Modal, Typography
} from "antd";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeftOutlined } from "@ant-design/icons";
import AuthService from "../utils/api/auth";
import { VSCategoriesData } from "../data/vs";
import "../scss/register.scss";
import StepCompany from "./Register/components/company";
import StepRoleSelection from "./Register/components/role";

const { Option } = Select;
const { Title, Paragraph } = Typography;

const StepPersonalForm = ({ form }) => (
  <div>
    <Title level={4}>Información Personal</Title>
    <Row gutter={16}>
      <Col span={12}>
        <Form.Item
          name="name"
          label="Nombre completo"
          rules={[
            { required: true, message: 'Ingresa tu nombre completo' },
            { min: 3, message: 'Mínimo 3 caracteres' },
            { max: 100, message: 'Máximo 100 caracteres' }
          ]}
        >
          <Input placeholder="Ej: Brian Manriquez" maxLength={100} autoComplete="off" />
        </Form.Item>
      </Col>
      <Col span={12}>
        <Form.Item
          name="phone"
          label="Teléfono"
          rules={[
            { required: true, message: 'Ingresa tu teléfono' },
            {
              pattern: /^[\d\s\-+()]{10,15}$/,
              message: 'Formato de teléfono inválido (10-15 dígitos)'
            }
          ]}
          normalize={(value) => value ? value.replace(/[^\d\s\-+()]/g, '') : value}
        >
          <Input placeholder="Ej: +52 55 1234 5678" maxLength={20} autoComplete="off" />
        </Form.Item>
      </Col>
    </Row>

    <Form.Item
      name="email"
      label="Correo electrónico"
      rules={[
        { required: true, message: 'Ingresa tu correo electrónico' },
        { type: 'email', message: 'Correo electrónico inválido' }
      ]}
      normalize={(value) => value ? value.trim().toLowerCase() : value}
    >
      <Input
        type="email"
        placeholder="ejemplo@correo.com"
        maxLength={100}
        autoComplete="off"
      />
    </Form.Item>

    <Row gutter={16}>
      <Col span={12}>
        <Form.Item
          name="password"
          label="Contraseña"
          rules={[
            { required: true, message: 'Ingresa una contraseña' },
            { min: 8, message: 'Mínimo 8 caracteres' },
            {
              pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/,
              message: 'Debe incluir al menos una mayúscula, una minúscula y un número'
            }
          ]}
        >
          <Input.Password
            placeholder="••••••••"
            maxLength={50}
            autoComplete="new-password"
          />
        </Form.Item>
      </Col>
      <Col span={12}>
        <Form.Item
          name="confirmPassword"
          label="Confirmar contraseña"
          dependencies={['password']}
          rules={[
            { required: true, message: 'Confirma tu contraseña' },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('password') === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error('Las contraseñas no coinciden'));
              },
            }),
          ]}
        >
          <Input.Password
            placeholder="••••••••"
            maxLength={50}
            autoComplete="new-password"
          />
        </Form.Item>
      </Col>
    </Row>
  </div>
);

const StepAddressForm = ({
  form,
  cities,
  country,
  loadingCities
}) => (
  <div>
    <Title level={4}>Dirección</Title>
    <Row gutter={16}>
      <Col span={12}>
        <Form.Item
          name="street"
          label="Calle"
          rules={[
            { required: true, message: 'Ingresa la calle' },
            { min: 3, message: 'Mínimo 3 caracteres' },
            { max: 100, message: 'Máximo 100 caracteres' }
          ]}
        >
          <Input placeholder="Ej: Av. Principal 123" maxLength={100} />
        </Form.Item>
      </Col>
      <Col span={12}>
        <Form.Item
          name="ext_num"
          label="Número exterior"
          rules={[
            { required: true, message: 'Ingresa el número exterior' },
            { max: 10, message: 'Máximo 10 caracteres' }
          ]}
        >
          <Input placeholder="Ej: 123" maxLength={10} />
        </Form.Item>
      </Col>
    </Row>

    <Row gutter={16}>
      <Col span={12}>
        <Form.Item
          name="int_num"
          label="Número interior (opcional)"
          rules={[{ max: 10, message: 'Máximo 10 caracteres' }]}
        >
          <Input placeholder="Ej: 456" maxLength={10} />
        </Form.Item>
      </Col>
      <Col span={12}>
        <Form.Item
          name="colony"
          label="Colonia"
          rules={[
            { required: true, message: 'Ingresa la colonia' },
            { min: 3, message: 'Mínimo 3 caracteres' },
            { max: 50, message: 'Máximo 50 caracteres' }
          ]}
        >
          <Input placeholder="Ej: Centro Histórico" maxLength={50} />
        </Form.Item>
      </Col>
    </Row>

    <Row gutter={16}>
      <Col span={12}>
        <Form.Item
          name="city"
          label="Ciudad"
          rules={[{ required: true, message: 'Selecciona una ciudad' }]}
        >
          <Select
            placeholder="Selecciona ciudad"
            loading={loadingCities}
            disabled={!form.getFieldValue('state')}
            showSearch
            filterOption={(input, option) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          >
            {cities.map(city => (
              <Option key={city.id} value={city.name}>
                {city.name}
              </Option>
            ))}
          </Select>
        </Form.Item>
      </Col>
      <Col span={12}>
        <Form.Item
          name="postal_code"
          label="Código Postal"
          rules={[
            { required: true, message: 'Ingresa el código postal' },
            {
              validator: (_, value) => {
                if (!value) return Promise.resolve();

                const postalCodePatterns = {
                  MX: /^\d{5}$/,
                  US: /^\d{5}(-\d{4})?$/,
                  CA: /^[A-Z]\d[A-Z] \d[A-Z]\d$/,
                };

                if (country && postalCodePatterns[country]) {
                  if (postalCodePatterns[country].test(value)) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error(`Formato inválido para ${country}`));
                }
                return Promise.resolve();
              }
            }
          ]}
          normalize={(value) => value ? value.toUpperCase().replace(/[^A-Z0-9-]/g, '') : value}
        >
          <Input placeholder="Ej: 32000" maxLength={10} />
        </Form.Item>
      </Col>
    </Row>
  </div>
);

export default function RegisterX() {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [load, setLoad] = useState(false);
  const [logo, setLogo] = useState({ logoList: [] });
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const categories = VSCategoriesData;
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(true);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState('MX');
  const [currentStep, setCurrentStep] = useState(0);
  const [initialLoadDone, setInitialLoadDone] = useState(false);

  const fetchCountries = useCallback(async () => {
    setLoadingCountries(true);
    try {
      const response = await fetch('https://countriesnow.space/api/v0.1/countries');
      const data = await response.json();

      if (data.error) {
        throw new Error(data.msg || 'Error al cargar países');
      }

      const formattedCountries = data.data.map(country => ({
        name: country.country,
        code: country.iso2 || country.iso3 || country.country.substring(0, 2).toUpperCase()
      })).sort((a, b) => {
        if (a.code === 'MX') return -1;
        if (b.code === 'MX') return 1;
        return a.name.localeCompare(b.name);
      });

      setCountries(formattedCountries);
    } catch (error) {
      console.error('Error fetching countries:', error);
      message.error('Error al cargar la lista de países. Intenta recargar la página.');

      const backupCountries = [
        { code: 'MX', name: 'México' },
        { code: 'US', name: 'Estados Unidos' },
        { code: 'CA', name: 'Canadá' },
        { code: 'ES', name: 'España' },
      ];
      setCountries(backupCountries);
    } finally {
      setLoadingCountries(false);
    }
  }, []);

  const handleStateChange = useCallback(async (stateName) => {
    const countryName = countries.find(c => c.code === selectedCountry)?.name;
    if (!countryName || !stateName) return;

    form.setFieldsValue({ city: undefined });
    setCities([]);

    setLoadingCities(true);
    try {
      const response = await fetch('https://countriesnow.space/api/v0.1/countries/state/cities', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          country: countryName,
          state: stateName
        })
      });

      const data = await response.json();

      if (data.data && Array.isArray(data.data)) {
        const citiesList = data.data.map((city, index) => ({
          name: city,
          id: city || index
        }));
        setCities(citiesList);

        if (selectedCountry === 'MX' && stateName.toLowerCase().includes('chihuahua')) {
          const juarez = citiesList.find(city =>
            city.name.toLowerCase().includes('juarez') ||
            city.name.toLowerCase().includes('juárez')
          );

          if (juarez) {
            form.setFieldsValue({
              city: juarez.name,
              postal_code: '32000'
            });
          }
        }
      }
    } catch (error) {
      console.error('Error fetching cities:', error);
      message.warning('No se pudieron cargar las ciudades.');
    } finally {
      setLoadingCities(false);
    }
  }, [countries, selectedCountry, form]);

  const handleCountryChange = useCallback(async (countryName) => {
    const selected = countries.find(c => c.name === countryName);
    if (!selected) return;

    setSelectedCountry(selected.code);
    form.setFieldsValue({
      state: undefined,
      city: undefined,
      postal_code: undefined
    });
    setStates([]);
    setCities([]);

    setLoadingStates(true);
    try {
      const response = await fetch('https://countriesnow.space/api/v0.1/countries/states', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ country: countryName })
      });

      const data = await response.json();

      if (data.data?.states && Array.isArray(data.data.states)) {
        const statesList = data.data.states.map((state, index) => ({
          name: state.name,
          id: state.id || state.name || index
        }));
        setStates(statesList);

        if (selected.code === 'MX' && statesList.length > 0) {
          const chihuahua = statesList.find(state =>
            state.name.toLowerCase().includes('chihuahua')
          );

          if (chihuahua) {
            form.setFieldsValue({ state: chihuahua.name });
            handleStateChange(chihuahua.name);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching states:', error);
      message.warning('No se pudieron cargar los estados.');
    } finally {
      setLoadingStates(false);
    }
  }, [countries, form, handleStateChange]);

  useEffect(() => {
    // Limpiar el formulario cuando se monta el componente
    form.resetFields();
  }, []);

  useEffect(() => {
    fetchCountries();
  }, [fetchCountries]);

  useEffect(() => {
    if (countries.length > 0 && selectedCountry === 'MX' && !initialLoadDone) {
      const mexico = countries.find(c => c.code === 'MX');
      if (mexico && !form.getFieldValue('state')) {
        form.setFieldsValue({ country: mexico.name });
        handleCountryChange(mexico.name);
        setInitialLoadDone(true);
      }
    }
  }, [countries, form, selectedCountry, handleCountryChange, initialLoadDone]);

  const handleOk = () => {
    setTermsAccepted(true);
    form.setFieldsValue({ terms: true });
    setIsModalVisible(false);
  };

  const handleCancel = () => {
    setTermsAccepted(false);
    form.setFieldsValue({ terms: false });
    setIsModalVisible(false);
  };

  const getBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });
  };

  const propsLogo = {
    onRemove: (file) => {
      setLogo(prev => ({
        logoList: prev.logoList.filter(f => f.uid !== file.uid)
      }));
    },
    beforeUpload: async (file) => {
      const isImage = file.type.startsWith('image/');
      if (!isImage) {
        message.error('Solo se permiten archivos de imagen');
        return Upload.LIST_IGNORE;
      }

      const isLt2M = file.size / 1024 / 1024 < 2;
      if (!isLt2M) {
        message.error('La imagen debe ser menor a 2MB');
        return Upload.LIST_IGNORE;
      }

      try {
        const preview = await getBase64(file);
        file.thumbUrl = preview;
        setLogo(prev => ({
          logoList: [...prev.logoList, file]
        }));
        return false;
      } catch (error) {
        message.error('Error al procesar la imagen');
        return Upload.LIST_IGNORE;
      }
    },
    onPreview: async (file) => {
      const src = file.thumbUrl || (await getBase64(file.originFileObj));
      const imgWindow = window.open();
      imgWindow?.document.write(`
        <html>
          <head>
            <title>Vista previa</title>
            <style>
              body { margin: 0; padding: 20px; display: flex; justify-content: center; align-items: center; min-height: 100vh; background: #f0f2f5; }
              img { max-width: 100%; max-height: 90vh; box-shadow: 0 4px 12px rgba(0,0,0,0.15); border-radius: 8px; }
            </style>
          </head>
          <body>
            <img src="${src}" />
          </body>
        </html>
      `);
    },
    fileList: logo.logoList,
    customRequest: ({ onSuccess }) => {
      setTimeout(() => {
        onSuccess("ok");
      }, 0);
    },
    maxCount: 1
  };

  const onFinish = async () => {
    setLoad(true);

    try {
      await form.validateFields();

      const values = form.getFieldsValue(true);
      console.log('Form values:', values);

      const logoFile = logo.logoList[0];
      if (!logoFile) {
        message.error("Debes subir un logo antes de continuar.");
        setLoad(false);
        return;
      }

      let base64Logo;
      try {
        base64Logo = await getBase64(logoFile.originFileObj || logoFile);
      } catch (err) {
        message.error("Error al procesar el logo.");
        setLoad(false);
        return;
      }

      const sanitize = (value) => {
        if (typeof value === 'string') {
          return value.trim();
        }
        return value;
      };

      const formData = new FormData();

      const baseFields = {
        name: values.name,
        email: values.email ? values.email.trim().toLowerCase() : '',
        phone: values.phone,
        password: values.password,
        bussiness: values.bussiness,
        rfc: values.rfc,
        country: selectedCountry,
        state: values.state,
        city: values.city,
        postal_code: values.postal_code,
        colony: values.colony,
        street: values.street,
        ext_num: values.ext_num,
        int_num: values.int_num,
        role: Number(values.role)
      };

      console.log('Email being registered:', baseFields.email);

      Object.entries(baseFields).forEach(([key, value]) => {
        formData.append(key, value ? sanitize(value) : '');
      });

      formData.append('logo', base64Logo);

      const role_data = values.role === 6 ? {
        url: values.url ? sanitize(values.url) : '',
        cat1: values.categories,
        iva: values.iva,
        terms: values.terms,
      } : values.role === 4 ? {
        pay_days: values.pay_days,
      } : {};

      const formDataObject = Object.fromEntries(formData);
      formDataObject.role_data = role_data;

      console.log('Request payload:', {
        ...formDataObject,
        password: '***',
        logo: '***'
      });

      const response = await AuthService.Register(formDataObject);

      if (response?.data?.success) {
        message.success("Registro exitoso, redirigiendo al login...");
        setTimeout(() => navigate("/"), 2000);
      } else {
        throw new Error(response?.data?.message || "Error en el registro");
      }

    } catch (error) {
      console.error('Registration error:', error);
      console.error('Error data:', error.data || error.response?.data);
      console.error('Error status:', error.status || error.response?.status);
      
      // El HTTPClient retorna error.response directamente, no un objeto con .response
      const errorData = error.data || error.response?.data;
      const errorMessage = errorData?.error || 
                          errorData?.message || 
                          error.message || 
                          "Hubo un error en el registro, inténtalo nuevamente.";
      
      console.log('Error message to display:', errorMessage);
      
      // Mostrar mensajes según el tipo de error
      if (errorMessage.includes('Concurrent connections') || errorMessage.includes('Message failed')) {
        // El usuario SÍ fue registrado, solo falló el email
        message.success("Registro exitoso. Redirigiendo al login...", 5);
        setTimeout(() => navigate("/"), 2000);
      } else if (errorMessage.includes('email address is already in use')) {
        message.error('Este correo electrónico ya está registrado. Intenta con otro correo.', 8);
      } else {
        message.error(errorMessage, 8);
      }
    } finally {
      setLoad(false);
    }
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <div>
            <Title level={4}>Ubicación</Title>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="country"
                  label="País"
                  rules={[{ required: true, message: 'Selecciona un país' }]}
                >
                  <Select
                    placeholder="Selecciona tu país"
                    loading={loadingCountries}
                    onChange={handleCountryChange}
                    showSearch
                    filterOption={(input, option) =>
                      option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                    }
                    value={form.getFieldValue('country') || (countries.find(c => c.code === 'MX')?.name)}
                  >
                    {countries.map(country => (
                      <Option key={country.code} value={country.name}>
                        {country.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="state"
                  label="Estado/Provincia"
                  rules={[{ required: true, message: 'Selecciona un estado' }]}
                >
                  <Select
                    placeholder="Selecciona estado"
                    loading={loadingStates}
                    onChange={handleStateChange}
                    disabled={!selectedCountry}
                    showSearch
                    filterOption={(input, option) =>
                      option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                    }
                  >
                    {states.map(state => (
                      <Option key={state.id} value={state.name}>
                        {state.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          </div>
        );
      case 1:
        return <StepPersonalForm form={form} />;
      case 2:
        return (
          <StepAddressForm
            form={form}
            cities={cities}
            country={selectedCountry}
            loadingCities={loadingCities}
          />
        );
      case 3:
        return <StepCompany form={form} logoProps={propsLogo} />;
      case 4:
        return (
          <StepRoleSelection
            form={form}
            termsAccepted={termsAccepted}
            setTermsAccepted={setTermsAccepted}
            setIsModalVisible={setIsModalVisible}
            categories={categories}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Row className="register-row">
      <img className="register-logo" src="/img/epno.png" alt="EP&O" />
      <Col xs={24} sm={12} className="register-hero-col">
        <div className="register-hero">
          <Typography.Title className="hero-title">
            TODO LO QUE TU ÁREA DE COMPRAS NECESITA, EN UN SOLO LUGAR
          </Typography.Title>
          <Typography.Paragraph className="hero-subtitle">
            Conecta con proveedores confiables y optimiza tu proceso de adquisición.
          </Typography.Paragraph>
          <img src='/img/dispositivos.png' width={'100%'} alt="Dispositivos" />
        </div>
      </Col>
      <Col xs={24} sm={12} className="register-form-col">
        <div className="register-form">
          <Title level={3} className="form-title">REGISTRO</Title>
          <Paragraph className='form-subtitle'>
            {currentStep === 0 ? "Selecciona tu ubicación" :
              currentStep === 1 ? "Información personal" :
                currentStep === 2 ? "Dirección" :
                  currentStep === 3 ? "Información de empresa" :
                    "Selección de rol"}
          </Paragraph>

          <Form
            form={form}
            name="register"
            onFinish={onFinish}
            layout="vertical"
            autoComplete="off"
            scrollToFirstError
            key="registration-form"
          >
            {renderCurrentStep()}

            {currentStep === 4 && (
              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={load}
                  block
                  size="large"
                >
                  Registrarme
                </Button>
              </Form.Item>
            )}
          </Form>

          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 24 }}>
            {currentStep > 0 && (
              <Button
                onClick={() => setCurrentStep(currentStep - 1)}
                icon={<ArrowLeftOutlined />}
              >
                Atrás
              </Button>
            )}
            {currentStep < 4 && (
              <Button
                type="primary"
                onClick={async () => {
                  try {
                    if (currentStep === 0) {
                      await form.validateFields(['country', 'state']);
                      if (!selectedCountry) {
                        throw new Error('Selecciona un país');
                      }
                    } else if (currentStep === 1) {
                      await form.validateFields(['name', 'phone', 'email', 'password', 'confirmPassword']);
                    } else if (currentStep === 2) {
                      await form.validateFields(['street', 'ext_num', 'colony', 'city', 'postal_code']);
                    } else if (currentStep === 3) {
                      await form.validateFields(['bussiness', 'rfc']);
                      if (logo.logoList.length === 0) {
                        throw new Error('Debes subir un logo');
                      }
                    }

                    setCurrentStep(currentStep + 1);
                  } catch (err) {
                    console.error('Validation error:', err);
                    message.warning("Por favor completa los campos requeridos correctamente.");
                  }
                }}
                disabled={currentStep === 0 && !selectedCountry}
              >
                Siguiente
              </Button>
            )}
          </div>

          <div style={{ marginTop: 16, textAlign: 'center' }}>
            <Paragraph>
              ¿Ya tienes una cuenta? <Link to="/login">Inicia sesión aquí</Link>
            </Paragraph>
          </div>
        </div>
      </Col>

      <Modal
        title="Términos y Condiciones"
        open={isModalVisible}
        onOk={handleOk}
        closeIcon={false}
        onCancel={handleCancel}
        maskClosable={false}
        width={800}
        footer={[
          <Button key="cancel" onClick={handleCancel}>
            Cancelar
          </Button>,
          <Button key="submit" type="primary" onClick={handleOk}>
            Aceptar Términos
          </Button>,
        ]}
      >
        <div
          style={{
            maxHeight: "400px",
            overflowY: "auto",
            padding: "10px",
            border: "1px solid #f0f0f0",
            borderRadius: "4px"
          }}
        >
          <p>
            <strong>Fecha de última actualización: 01/Abr/2025</strong>
          </p>
          <h4>CAPÍTULO I. ACEPTACIÓN Y DEFINICIONES</h4>
          <p><strong>1. Aceptación de los Términos</strong><br />
          Al acceder, registrarse o utilizar los servicios de la plataforma digital EP&O Marketplace (en adelante, "la Plataforma"), usted acepta quedar legalmente obligado por estos Términos y Condiciones Generales de Uso (en adelante, "los Términos"), así como por las disposiciones legales aplicables en los Estados Unidos Mexicanos, incluyendo pero no limitado a la Ley Federal de Protección al Consumidor, el Código de Comercio, la Ley Federal de Protección de Datos Personales en Posesión de los Particulares, la Ley de Propiedad Industrial, la Ley Federal del Derecho de Autor y el Código Civil Federal.</p>

          <p>Si el Usuario no está de acuerdo con los Términos, deberá abstenerse de utilizar la Plataforma.</p>

          <p><strong>2. Definiciones</strong><br />
          Para efectos del presente documento, se entenderá por:</p>
          <p>2.1 EP&O: EPNO Corporation S. de R.L. de C.V., con domicilio en Cd. Juárez, Chihuahua, México, propietaria, titular y operadora de la Plataforma.<br />
          2.2 Plataforma: Sitio web y/o aplicación móvil que opera bajo el dominio www.epno.com.mx y otros subdominios vinculados.<br />
          2.3 Usuario: Toda persona física o moral que accede, navega, utiliza o se registra en la Plataforma, sea en calidad de Cliente o Proveedor.<br />
          2.4 Cliente: Usuario que utiliza la Plataforma con el objeto de adquirir productos o servicios ofrecidos por los Proveedores.<br />
          2.5 Proveedor: Usuario que ofrece productos o servicios industriales a través de la Plataforma para ser adquiridos por Clientes.<br />
          2.6 Orden: Transacción iniciada mediante una solicitud de cotización y/o compra dentro de la Plataforma.<br />
          2.7 Contenido: Toda información, texto, imagen, dato, archivo o documento publicado por cualquier Usuario.</p>

          <h4>CAPÍTULO II. OBJETO DEL SERVICIO Y ALCANCE LEGAL</h4>
          <p><strong>3. Objeto del Servicio</strong><br />
          La Plataforma tiene como objeto principal facilitar la conexión entre Clientes industriales y Proveedores especializados, permitiendo la solicitud de cotizaciones, la comparación de precios, condiciones comerciales, y eventualmente la colocación de órdenes de compra. EP&O actúa como intermediario tecnológico y comercial, sin asumir responsabilidad directa sobre los productos, salvo que se indique expresamente lo contrario.</p>

          <p><strong>4. Relación contractual</strong><br />
          4.1 Las relaciones comerciales entre Clientes y Proveedores se regirán por contratos civiles o mercantiles entre dichos Usuarios.<br />
          4.2 EP&O no garantiza la solvencia, capacidad técnica, cumplimiento o veracidad de la información publicada por los Usuarios.<br />
          4.3 EP&O podrá intervenir como tercero neutral para la resolución de disputas cuando ambas partes lo autoricen expresamente.</p>

          <h4>CAPÍTULO III. REGISTRO, OBLIGACIONES Y CONDUCTA DE LOS USUARIOS</h4>
          <p><strong>5. Registro y autenticación</strong><br />
          5.1 Para acceder a funcionalidades clave, los Usuarios deberán registrarse mediante formularios en línea y aceptar el Aviso de Privacidad.<br />
          5.2 La información proporcionada deberá ser veraz, fidedigna y actualizada.<br />
          5.3 EP&O se reserva el derecho de verificar la identidad y actividad de los Usuarios mediante mecanismos automatizados y humanos.</p>

          <h4>CAPÍTULO IV. TRANSACCIONES, PAGOS Y LOGÍSTICA</h4>
          <p><strong>8. Precios y Cotizaciones</strong><br />
          8.1 Los Proveedores deberán publicar precios claros, condiciones de entrega, tiempo de fabricación y garantías aplicables.<br />
          8.2 Toda cotización enviada a través de la Plataforma constituye una oferta vinculante por el plazo que indique el Proveedor.</p>

          <p><strong>9. Pago y Facturación</strong><br />
          9.1 El Cliente se obliga a cumplir con los pagos según las condiciones pactadas.<br />
          9.2 En caso de que EP&O actúe como intermediario financiero, podrá retener comisiones de servicio.</p>

          <h4>CAPÍTULO V. CANCELACIONES, RECLAMACIONES Y RESPONSABILIDAD</h4>
          <p><strong>11. Cancelaciones y Devoluciones</strong><br />
          11.1 Cada Proveedor deberá establecer y publicar su propia política de cancelación.<br />
          11.2 Los Clientes podrán activar un procedimiento de reclamación mediante el sistema interno de la Plataforma dentro de los 5 días hábiles posteriores a la entrega.</p>

          <h4>CAPÍTULO VI. PROPIEDAD INTELECTUAL Y CONFIDENCIALIDAD</h4>
          <p><strong>13. Derechos de Propiedad Intelectual.</strong><br />
          13.1 Todos los derechos de diseño, software, marcas, logotipos, código fuente y base de datos son propiedad de EP&O.<br />
          13.2 Se prohíbe la reproducción, distribución o uso comercial sin autorización expresa.</p>

          <h4>CAPÍTULO VII. DISPOSICIONES FINALES</h4>
          <p><strong>16. Modificaciones</strong><br />
          16.1 EP&O podrá modificar unilateralmente los Términos, los cuales serán publicados en www.epno.com.mx.</p>

          <p><strong>17. Ley Aplicable y Jurisdicción</strong><br />
          17.1 Este acuerdo se rige por las leyes de los Estados Unidos Mexicanos.<br />
          17.2 Para cualquier controversia, las partes se someten a los tribunales competentes de Ciudad Juárez, Chihuahua, renunciando expresamente a cualquier otro fuero que pudiera corresponderles.</p>

          <p><strong>18. Contacto</strong><br />
          Cualquier duda o notificación deberá dirigirse a contacto@epno.com.mx</p>
        </div>
      </Modal>
    </Row>
  );
}